import React from "react";
import { X, Package, MessageCircle, DollarSign, Star, Heart } from "lucide-react";
import { formatPrice } from "../../utils/formatters";
import { getSoldState } from "../../utils/productSold";
import ImageCarousel from "../Shop/ImageCarousel";

/**
 * ListingDetailModal - Full listing details with images, description, and visitor actions
 * 
 * Props:
 * - open: boolean
 * - onClose: () => void
 * - listing: full product/listing object (from detail fetch)
 * - loading: boolean (show skeleton while fetching)
 * - error: string | null
 * - isFavorite: boolean
 * - onToggleFavorite: (id) => void
 * - onMessage: (listing) => void
 * - onOffer: (listing) => void
 * - onStudioClick: (slug) => void
 */
export default function ListingDetailModal({
  open,
  onClose,
  listing,
  loading,
  error,
  isFavorite,
  onToggleFavorite,
  onMessage,
  onOffer,
  onStudioClick,
}) {
  if (!open) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-[#1a1b1e] rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden border border-white/10">
        {/* Header */}
        <div className="sticky top-0 bg-[#1a1b1e] border-b border-white/10 px-6 py-4 flex items-center justify-between z-10">
          <h2 className="text-xl font-bold text-white">
            {loading ? "Loading..." : listing?.title || "Listing Details"}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg transition"
            title="Close"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-80px)]">
          {loading && <LoadingSkeleton />}
          
          {error && (
            <div className="p-6 text-center">
              <div className="text-red-400 mb-2">Failed to load listing</div>
              <div className="text-sm text-white/50">{error}</div>
              <button
                onClick={onClose}
                className="mt-4 px-4 py-2 rounded-lg bg-white/10 hover:bg-white/15 transition"
              >
                Close
              </button>
            </div>
          )}

          {!loading && !error && listing && (
            <ListingDetailContent
              listing={listing}
              isFavorite={isFavorite}
              onToggleFavorite={onToggleFavorite}
              onMessage={onMessage}
              onOffer={onOffer}
              onStudioClick={onStudioClick}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="p-6 space-y-4 animate-pulse">
      <div className="w-full h-64 bg-white/10 rounded-lg" />
      <div className="h-6 bg-white/10 rounded w-3/4" />
      <div className="h-4 bg-white/10 rounded w-1/2" />
      <div className="space-y-2">
        <div className="h-4 bg-white/10 rounded" />
        <div className="h-4 bg-white/10 rounded" />
        <div className="h-4 bg-white/10 rounded w-5/6" />
      </div>
    </div>
  );
}

function ListingDetailContent({
  listing,
  isFavorite,
  onToggleFavorite,
  onMessage,
  onOffer,
  onStudioClick,
}) {
  const images =
    (Array.isArray(listing.image_gallery) && listing.image_gallery.length > 0
      ? listing.image_gallery
      : listing.main_image_url
      ? [listing.main_image_url]
      : []) || [];

  const hasStock = listing.stock_qty === null || listing.stock_qty > 0;
  const stockLabel =
    listing.stock_qty === null
      ? "In stock"
      : listing.stock_qty === 0
      ? "Out of stock"
      : `${listing.stock_qty} in stock`;

  const soldState = getSoldState(listing);
  const showSoldWatermark = soldState.isSold;
  const soldLocked = soldState.isSold;

  return (
    <div className="p-6 space-y-6">
      {/* Image Gallery */}
      <div className="relative">
        {images.length > 0 ? (
          <ImageCarousel
            images={images}
            alt={listing.title || "Product"}
            aspect="16/10"
            showDots={true}
            showCounter={true}
          />
        ) : (
          <div
            className="w-full flex items-center justify-center bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-lg"
            style={{ aspectRatio: "16 / 10" }}
          >
            <Package className="w-16 h-16 text-white/20" />
          </div>
        )}

        {/* Favorite button */}
        <button
          onClick={() => onToggleFavorite(listing.id)}
          className={`absolute top-3 right-3 p-2 rounded-full backdrop-blur-md transition-all ${
            isFavorite
              ? "bg-red-600 text-white"
              : "bg-black/30 text-white/70 hover:bg-black/50 hover:text-white"
          }`}
          title={isFavorite ? "Remove from favorites" : "Add to favorites"}
        >
          <Heart className={`w-5 h-5 ${isFavorite ? "fill-current" : ""}`} />
        </button>

        {/* Sold watermark */}
        {showSoldWatermark && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-[rgba(209,34,34,0.4)] to-[#b51717]/70 backdrop-blur-sm rounded-lg" />
            <div className="relative text-center">
              <span className="px-6 py-2 text-2xl font-black tracking-[0.35em] uppercase text-white drop-shadow-[0_0_20px_rgba(0,0,0,0.4)]">
                SOLD
              </span>
              {soldState.isInGrace && (
                <div className="text-xs uppercase text-white/80 tracking-[0.25em] mt-2">
                  Auto-unpublishes in {soldState.graceHoursLeft || 0}h
                </div>
              )}
            </div>
          </div>
        )}

        {/* Stock badges */}
        {!hasStock && (
          <div className="absolute top-3 left-3 px-3 py-1 rounded-md bg-red-600 text-white text-sm font-semibold">
            Out of Stock
          </div>
        )}
        {hasStock && listing.stock_qty !== null && listing.stock_qty <= 3 && (
          <div className="absolute top-3 left-3 px-3 py-1 rounded-md bg-orange-600 text-white text-sm font-semibold">
            Only {listing.stock_qty} left
          </div>
        )}
      </div>

      {/* Title and Price */}
      <div>
        <h1 className="text-2xl font-bold text-white mb-2">{listing.title}</h1>
        <div className="text-3xl font-bold text-green-400 mb-3">
          {showSoldWatermark ? "Sold" : formatPrice(listing.price_cents, listing.currency)}
        </div>
      </div>

      {/* Studio Info */}
      {listing.studio && (
        <button
          onClick={() => onStudioClick(listing.studio.slug)}
          className="flex items-center gap-2 text-green-500 hover:text-green-400 transition-colors group"
        >
          <Star className="w-4 h-4" />
          <span className="font-semibold group-hover:underline">{listing.studio.name}</span>
          {listing.studio.city && (
            <span className="text-white/50 text-sm">• {listing.studio.city}</span>
          )}
        </button>
      )}

      {/* Description */}
      {listing.description && (
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-white">Description</h3>
          <p className="text-white/70 leading-relaxed whitespace-pre-wrap">
            {listing.description}
          </p>
        </div>
      )}

      {/* Product Details */}
      <div className="grid grid-cols-2 gap-4 pt-4 border-t border-white/10">
        {listing.category && (
          <div>
            <div className="text-xs text-white/50 uppercase tracking-wider mb-1">Category</div>
            <div className="text-white">{listing.category}</div>
          </div>
        )}
        {listing.condition && (
          <div>
            <div className="text-xs text-white/50 uppercase tracking-wider mb-1">Condition</div>
            <div className="text-white">{listing.condition}</div>
          </div>
        )}
        {listing.location && (
          <div>
            <div className="text-xs text-white/50 uppercase tracking-wider mb-1">Location</div>
            <div className="text-white">{listing.location}</div>
          </div>
        )}
        <div>
          <div className="text-xs text-white/50 uppercase tracking-wider mb-1">Availability</div>
          <div className="text-white">{stockLabel}</div>
        </div>
      </div>

      {/* Action Buttons */}
      {!soldLocked && (
        <div className="flex gap-3 pt-4 border-t border-white/10">
          <button
            onClick={() => onMessage(listing)}
            className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold transition-colors"
          >
            <MessageCircle className="w-5 h-5" />
            Message Seller
          </button>
          <button
            onClick={() => onOffer(listing)}
            className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-amber-600 hover:bg-amber-700 text-white font-semibold transition-colors"
          >
            <DollarSign className="w-5 h-5" />
            Make Offer
          </button>
        </div>
      )}

      {soldLocked && (
        <div className="flex items-center justify-center gap-2 p-4 rounded-lg bg-red-600/20 border border-red-600/30 text-red-400">
          <span className="font-semibold">This item has been sold</span>
        </div>
      )}
    </div>
  );
}
